import { Badge } from "@screener/uikit/components/badge";
import { RollingNumber } from "@screener/uikit/components/rolling-number";
import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import { cn } from "@screener/uikit/lib/utils";
import { memo } from "react";
import { Ago } from "@/density/elapsed";
import type { StreamState } from "@/density/stream-state";
import type { StreamStore } from "@/density/use-density-stream";
import { useStreamSelector } from "@/density/use-stream-selector";
import type { ConnectionStatus } from "@/hooks/use-ipc-connection";
import { countNotLive, notLiveSummary } from "@/not-live-summary";

function selectLiveInstruments(state: StreamState): number {
  return state.instruments.filter((entry) => entry.status === "live").length;
}

function selectInstrumentCount(state: StreamState): number {
  return state.instruments.length;
}

function selectNotLiveCount(state: StreamState): number {
  return countNotLive(state.instruments, state.marketsDown);
}

function selectNotLiveSummary(state: StreamState): string {
  return notLiveSummary(state.instruments, state.marketsDown);
}

/**
 * «X из Y инструментов отдают данные» и число без данных — лист со своими
 * примитивами: статусы инструментов меняются часто, и верхняя полоса от них
 * целиком не перерисовывается.
 */
function InstrumentSummary({ store }: { store: StreamStore }) {
  const liveCount = useStreamSelector(store, selectLiveInstruments);
  const instrumentCount = useStreamSelector(store, selectInstrumentCount);
  // Инструменты лежащего рынка в плашку не входят (§132 п. 5), поэтому
  // разность «Y минус X» её больше не считает.
  const notLive = useStreamSelector(store, selectNotLiveCount);
  const notLiveText = useStreamSelector(store, selectNotLiveSummary);
  return (
    <>
      <span data-live-leaf className="truncate text-xs text-muted-foreground tabular-nums">
        {/* Прокрутка цифр — та же, что у «N из M» полосы фильтров. */}
        <RollingNumber value={liveCount} /> из <RollingNumber value={instrumentCount} />{" "}
        инструментов отдают данные
      </span>
      {notLive > 0 ? (
        // Какие инструменты без данных — в подсказке (решение владельца
        // 14.10.2026): плашка фокусируется, и список доступен с клавиатуры.
        <Tooltip>
          <TooltipTrigger
            render={
              <Badge
                variant="outline"
                tabIndex={0}
                className="h-[18px] shrink-0 cursor-default border-transparent bg-warning-wash px-1.5 text-[11px] tracking-wider text-warning uppercase focus-visible:outline-2 focus-visible:outline-ring"
              />
            }
          >
            <RollingNumber value={notLive} /> без данных
          </TooltipTrigger>
          <TooltipContent
            data-slot="not-live-list"
            className="max-w-[420px] text-left whitespace-pre-line"
          >
            {notLiveText}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </>
  );
}

interface ConnectionStatusGroupProps {
  store: StreamStore;
  connectionStatus: ConnectionStatus;
  /** Момент, с которого держится текущее состояние соединения. */
  connectionSinceMs: number;
  /**
   * Узел живой области исхода копирования тикера: его текст рендерит экран
   * плотностей порталом (design.md stage9-redesign-chrome, Д4).
   */
  noticeSlotRef: (node: HTMLSpanElement | null) => void;
}

/**
 * Связь и число инструментов в верхней полосе каркаса (§88 п. 7): одна полоса
 * на все экраны. Пропсы — примитивы и стабильные ссылки, так что каркас её не
 * перерисовывает кадрами потока; секунды давности тикают в листе `Ago`.
 */
export const ConnectionStatusGroup = memo(function ConnectionStatusGroup({
  store,
  connectionStatus,
  connectionSinceMs,
  noticeSlotRef,
}: ConnectionStatusGroupProps) {
  const stale = connectionStatus !== "open";
  return (
    <div data-connection-strip className="flex min-w-0 items-center gap-3.5">
      {/* Цвета соединения — не доменные: `bid`, `ask` и `mm` §9 отданы
          стороне стакана и признаку ММ. Отсюда свой токен `online`; при
          разрыве точка и слово — цветом ошибки, и слово объявляется. */}
      <span
        className={cn(
          "inline-flex shrink-0 items-center gap-1.5 text-xs leading-none font-medium",
          stale && "text-destructive",
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            "size-1.5 rounded-full",
            stale ? "bg-destructive" : "animate-online-pulse bg-online motion-reduce:animate-none",
          )}
        />
        {/* Объявляется только слово: секунды тикают каждую секунду, и
            читающий с экрана слышал бы их вместо самой новости. */}
        <span aria-live="polite">{stale ? "Нет связи" : "На связи"}</span>
        {stale ? (
          <span aria-hidden="true" className="font-normal">
            {" "}
            — данные устарели <Ago tsMs={connectionSinceMs} /> назад
          </span>
        ) : null}
      </span>
      <InstrumentSummary store={store} />
      {/* Исход копирования тикера §9 (web-density-table «Отказ буфера
          обмена назван») — своя область `aria-live="polite"`, а не общая со
          связью: связь и копирование — разные новости. Область смонтирована
          всегда, меняется только текст внутри: регион, вставленный в DOM сразу
          с содержимым, объявляется ненадёжно (review stage5-round-price, п. 2). */}
      <span ref={noticeSlotRef} aria-live="polite" className="min-w-0 truncate text-xs" />
    </div>
  );
});
